/**
 * CI-21979 — Normalización centralizada de la configuración de decimales
 * (`numberOfDecimalsBE`, entregada por CMS Storefront).
 *
 * Una sola fuente de verdad reutilizada por el mapper del profile, el pipe
 * visual (amCurrency) y el interceptor que envía el header numberOfDecimalsBE.
 *
 * No hay valor por defecto: si la configuración está ausente o es inválida, se
 * devuelve `undefined` y cada consumidor conserva el comportamiento previo
 * (la UI mantiene su formato anterior y el interceptor no agrega el header).
 */

/** Únicos valores válidos confirmados para la cantidad de decimales. */
const VALID_NUMBER_OF_DECIMALS = [0, 1, 2];

/**
 * Normaliza la cantidad de decimales.
 *
 * Devuelve el número (0, 1 o 2) cuando el valor es uno de esos enteros, ya sea
 * como número o como cadena numérica ("0", "1", "2"). En cualquier otro caso
 * devuelve `undefined`:
 *  - ausente (null / undefined);
 *  - cadena vacía o de espacios;
 *  - no numérico (NaN, "abc");
 *  - negativo (-1);
 *  - decimal no entero (1.5);
 *  - fuera del rango permitido (3, "3", etc.).
 *
 * @param value valor recibido desde CMS (número o cadena numérica).
 * @returns 0 | 1 | 2, o `undefined` si no es válido.
 */
export function normalizeNumberOfDecimals(value: unknown): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }

  const parsed = typeof value === 'number' ? value : Number(value);

  if (!Number.isInteger(parsed) || !VALID_NUMBER_OF_DECIMALS.includes(parsed)) {
    return undefined;
  }

  return parsed;
}
